import {HttpClient} from '@angular/common/http';
import {Injectable, OnDestroy} from '@angular/core';
import {MatSnackBar} from '@angular/material/snack-bar';
import {BaseService} from 'app/services/base.service';
import {WebSocketService} from 'app/services/websocket/websocket.service';
import {Callback} from 'app/utils/typedefs';
import {UUID} from 'app/utils/uuid';
import {environment} from 'environments/environment';
import {Observable, ReplaySubject, Subscription} from 'rxjs';
import {catchError, map, take} from 'rxjs/operators';
import {InternalReferenceState, TargetVersionType} from './internal-reference-state';

@Injectable({
  providedIn: 'root',
})
export class InternalReferenceStateService extends BaseService implements OnDestroy {
  private static readonly INTERNAL_REFERENCE_STATE_WEBSOCKET_TOPIC = '/topic/internalReferenceState';

  private readonly _baseUrl: string = `${environment.apiHost}/internal-reference-state`;

  private _websocketSubscriptions: Subscription[] = [];

  private _internalReferenceStateReplaySubject: ReplaySubject<InternalReferenceState[]> = new ReplaySubject<
    InternalReferenceState[]
  >(1);

  constructor(
    private _websocketService: WebSocketService,
    protected _snackbar: MatSnackBar,
    private _http: HttpClient
  ) {
    super(_snackbar);

    this._subscriptions.push(
      this._websocketService.onConnection((connected: boolean) => {
        this._unsubscribeWebsocketSubscriptions();

        if (connected) {
          this._websocketSubscriptions.push(
            this._websocketService.subscribe(
              InternalReferenceStateService.INTERNAL_REFERENCE_STATE_WEBSOCKET_TOPIC,
              (json: InternalReferenceState) => {
                const internalReferenceState: InternalReferenceState = InternalReferenceState.deserialise(json);
                this._handleWebsocketEvent(internalReferenceState);
              }
            )
          );
        }
      })
    );
  }

  public onChange(callback: Callback<InternalReferenceState[]>): Subscription {
    return this._internalReferenceStateReplaySubject.subscribe(callback);
  }

  public ngOnDestroy(): void {
    super.ngOnDestroy();
    this._unsubscribeWebsocketSubscriptions();
  }

  private _unsubscribeWebsocketSubscriptions(): void {
    this._websocketSubscriptions.splice(0).forEach((s: Subscription) => s.unsubscribe());
  }

  /**
   * Handles a websocket event for a change to the internalReferenceStates
   *
   * @param incomingInternalReferenceState The internal reference state to be added or updated
   */
  private _handleWebsocketEvent(incomingInternalReferenceState: InternalReferenceState): void {
    this._internalReferenceStateReplaySubject
      .pipe(take(1))
      .subscribe((currentInternalReferenceList: InternalReferenceState[]) => {
        const index = currentInternalReferenceList.findIndex(
          (item: InternalReferenceState) =>
            item.sourceDocumentId.value === incomingInternalReferenceState.sourceDocumentId.value &&
            item.targetDocumentId.value === incomingInternalReferenceState.targetDocumentId.value
        );

        let updatedList: InternalReferenceState[];

        if (index !== -1) {
          const existing = currentInternalReferenceList[index];

          if (existing.targetVersion !== incomingInternalReferenceState.targetVersion) {
            updatedList = currentInternalReferenceList;
            updatedList[index].targetVersion = incomingInternalReferenceState.targetVersion;
            this._internalReferenceStateReplaySubject.next(updatedList);
          }
        } else {
          updatedList = [...currentInternalReferenceList, incomingInternalReferenceState];
          this._internalReferenceStateReplaySubject.next(updatedList);
        }
      });
  }

  /**
   * Creates an internal reference state from the given sourceDocumentId to the targetDocumentId with the
   *  provided target version type.
   *
   * @param sourceDocumentId The source document id to create the reference state from
   * @param targetDocumentId The target document id to create the reference state to
   * @param targetVersion The type of version the reference is pointing to
   * @returns A promise resolving to the created internal reference state.
   */
  public updateTargetVersion(
    sourceDocumentId: UUID,
    targetDocumentId: UUID,
    targetVersion: TargetVersionType
  ): Promise<any> {
    const params: {[param: string]: string} = {
      sourceDocumentId: sourceDocumentId.value,
      targetDocumentId: targetDocumentId.value,
      targetVersion: targetVersion,
    };
    return this._http
      .post(`${this._baseUrl}`, {}, {params})
      .pipe(
        map((response: any) => InternalReferenceState.deserialise(response)),
        catchError((error: any) => {
          this._handleError(error, 'Failed to create internal reference state');
          return Promise.reject(error);
        })
      )
      .toPromise();
  }

  /**
   * Gets all incoming and outgoing internal reference states for a given document.
   *
   * @param documentId The documentId to get internal reference states for.
   * @returns An observable of the internal reference states.
   */
  public fetchAllForDocument(documentId: UUID): Observable<InternalReferenceState[]> {
    this._http
      .get<InternalReferenceState[]>(`${this._baseUrl}/${documentId.value}`)
      .pipe(map((response: any[]) => response.map((json: any) => InternalReferenceState.deserialise(json))))
      .subscribe((data: InternalReferenceState[]) => {
        this._internalReferenceStateReplaySubject.next(data);
      });

    return this._internalReferenceStateReplaySubject.asObservable();
  }
}
